import type { AxiosInstance } from 'axios';
import { internalApiClient } from '@/marketing/infrastructure/http/httpClient';
import { pathSegment } from '@/marketing/lib/pathSegment';
import type {
  CampaignScoutingResponse,
  ScoutingPage,
  ScoutingPageParams,
  ScoutingExportParams,
  ScoutingNotesRequest,
  ScoutingNotesResponse,
  ScoutingDecisionRequest,
  ScoutingSelectionRequest,
  ScoutingSelectionResponse,
} from '@/marketing/models/campaignScouting';

import type { ScoutingProductsRequest, ScoutingProductsResponse } from '@/marketing/models/campaignScoutingDetails';

export class CampaignScoutingAPIClient {
  constructor(private readonly client: AxiosInstance = internalApiClient) {}
  public async list(campaignId: string): Promise<CampaignScoutingResponse> {
    return (await this.client.get<CampaignScoutingResponse>(this.path(campaignId))).data;
  }
  public async page(campaignId: string, params: ScoutingPageParams, signal?: AbortSignal): Promise<ScoutingPage> {
    return (
      await this.client.get<ScoutingPage>(`${this.path(campaignId)}/page`, { params, ...(signal ? { signal } : {}) })
    ).data;
  }
  public async exportAll(
    campaignId: string,
    params: ScoutingExportParams,
    signal?: AbortSignal,
  ): Promise<ScoutingPage> {
    return (
      await this.client.get<ScoutingPage>(`${this.path(campaignId)}/export`, { params, ...(signal ? { signal } : {}) })
    ).data;
  }
  public async decide(campaignId: string, influencerId: string, request: ScoutingDecisionRequest): Promise<void> {
    await this.client.patch(`${this.path(campaignId)}/${pathSegment(influencerId)}`, request);
  }
  public async select(campaignId: string, request: ScoutingSelectionRequest): Promise<ScoutingSelectionResponse> {
    return (await this.client.post<ScoutingSelectionResponse>(`${this.path(campaignId)}/select`, request)).data;
  }
  public async setProducts(
    campaignId: string,
    influencerId: string,
    request: ScoutingProductsRequest,
  ): Promise<ScoutingProductsResponse> {
    return (
      await this.client.patch<ScoutingProductsResponse>(
        `${this.path(campaignId)}/${pathSegment(influencerId)}/products`,
        request,
      )
    ).data;
  }
  public async setNotes(
    campaignId: string,
    influencerId: string,
    request: ScoutingNotesRequest,
  ): Promise<ScoutingNotesResponse> {
    return (
      await this.client.patch<ScoutingNotesResponse>(
        `${this.path(campaignId)}/${pathSegment(influencerId)}/notes`,
        request,
      )
    ).data;
  }
  private path(campaignId: string): string {
    return `/influencer/campaigns/${pathSegment(campaignId)}/scouting`;
  }
}
export const campaignScoutingAPIClient = new CampaignScoutingAPIClient();
